import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

const EDGE_SLOP = 2;
const SCROLL_RATIO = 0.75;

/** Mac / iOS already get easy horizontal scroll (trackpad / touch). */
function isApplePointerDevice() {
    if (typeof navigator === 'undefined') return true;
    const platform = navigator.platform || '';
    const ua = navigator.userAgent || '';
    return /Mac|iPhone|iPod|iPad/i.test(platform) || /\bMac OS X\b/.test(ua);
}

function readOverflow(track: HTMLElement) {
    const content = track.firstElementChild;
    if (!content) return { canPrev: false, canNext: false };

    const trackRect = track.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    return {
        // Inline-start edge is on the right in RTL: content past that edge → can go back
        canPrev: contentRect.right > trackRect.right + EDGE_SLOP,
        // Inline-end edge is on the left in RTL: content past that edge → can go forward
        canNext: contentRect.left < trackRect.left - EDGE_SLOP,
    };
}

/**
 * Sign to add to scrollLeft to move content toward inline-end (next).
 * RTL scrollLeft conventions differ across engines, so probe once.
 */
function detectNextScrollSign(track: HTMLElement): 1 | -1 {
    const content = track.firstElementChild;
    if (!content) return -1;

    const saved = track.scrollLeft;
    const before = content.getBoundingClientRect().left;

    track.scrollLeft = saved - 10;
    let after = content.getBoundingClientRect().left;
    if (after > before + 0.5) {
        track.scrollLeft = saved;
        return -1;
    }
    if (after < before - 0.5) {
        track.scrollLeft = saved;
        return 1;
    }

    track.scrollLeft = saved + 10;
    after = content.getBoundingClientRect().left;
    track.scrollLeft = saved;
    if (after > before + 0.5) return 1;
    if (after < before - 0.5) return -1;
    return -1;
}

/**
 * Native horizontal scroll: hidden scrollbar, edge fade, prev/next
 * buttons only on non-Apple pointers (no easy trackpad / touch).
 * Wrap a single `w-max` child so overflow detection sees the full row.
 */
export function HomeHScrollTrack({
    children,
    prevLabel,
    nextLabel,
    className,
    resetKey,
}: {
    children: ReactNode;
    prevLabel: string;
    nextLabel: string;
    className?: string;
    /** Re-bind overflow listeners when the row contents change. */
    resetKey?: string;
}) {
    const trackRef = useRef<HTMLDivElement>(null);
    const nextSignRef = useRef<1 | -1>(-1);
    const [needsScrollButtons, setNeedsScrollButtons] = useState(false);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);

    useEffect(() => {
        if (isApplePointerDevice()) return;
        setNeedsScrollButtons(true);
    }, []);

    useEffect(() => {
        if (!needsScrollButtons) return;
        const track = trackRef.current;
        if (!track) return;

        let signDetected = false;

        function sync() {
            if (!signDetected && track!.scrollWidth > track!.clientWidth + EDGE_SLOP) {
                nextSignRef.current = detectNextScrollSign(track!);
                signDetected = true;
            }
            const next = readOverflow(track!);
            setCanPrev(next.canPrev);
            setCanNext(next.canNext);
        }

        sync();
        track.addEventListener('scroll', sync, { passive: true });
        const ro = new ResizeObserver(sync);
        ro.observe(track);
        if (track.firstElementChild) ro.observe(track.firstElementChild);

        return () => {
            track.removeEventListener('scroll', sync);
            ro.disconnect();
        };
    }, [needsScrollButtons, resetKey]);

    function scroll(direction: 'prev' | 'next') {
        const track = trackRef.current;
        if (!track) return;

        const amount = Math.max(200, Math.round(track.clientWidth * SCROLL_RATIO));
        const sign = direction === 'next' ? nextSignRef.current : -nextSignRef.current;
        track.scrollBy({ left: sign * amount, behavior: 'smooth' });
    }

    const showControls = needsScrollButtons && (canPrev || canNext);

    return (
        <div className={cn('flex min-w-0 items-center gap-1', className)}>
            <div
                ref={trackRef}
                tabIndex={0}
                className="home-h-scroll-track min-w-0 flex-1 overflow-x-auto overscroll-x-contain"
            >
                {children}
            </div>
            {showControls ? (
                <div className="flex shrink-0 items-center gap-0.5 pe-2">
                    <button
                        type="button"
                        className="inline-flex size-8 items-center justify-center text-white/55 transition-colors hover:text-white disabled:pointer-events-none disabled:opacity-25"
                        disabled={!canPrev}
                        aria-label={prevLabel}
                        onClick={() => scroll('prev')}
                    >
                        <ChevronRight className="size-4" aria-hidden />
                    </button>
                    <button
                        type="button"
                        className="inline-flex size-8 items-center justify-center text-white/55 transition-colors hover:text-white disabled:pointer-events-none disabled:opacity-25"
                        disabled={!canNext}
                        aria-label={nextLabel}
                        onClick={() => scroll('next')}
                    >
                        <ChevronLeft className="size-4" aria-hidden />
                    </button>
                </div>
            ) : null}
        </div>
    );
}
